import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './chatModal.css';
import './widgetPreview.css';
import './widgetCustomizationTab.css';

// Error messages for different error types from the backend
const buildErrorMessages = (t) => ({
  rate_limit: t('chat_err_rate_limit'),
  timeout: t('chat_err_timeout'),
  internal_error: t('chat_err_internal'),
  validation_error: t('chat_err_validation'),
  bot_not_found: t('chat_err_bot_not_found'),
  default: t('chat_err_default')
});

export default function BotChatStep({ bot, themeColor, height = 450 }) {
  const { t } = useTranslation();
  const accent = themeColor || '#5616ea';
  const [userInput, setUserInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [predefinedQuestions, setPredefinedQuestions] = useState([]);
  const [animateIndex, setAnimateIndex] = useState(0);
  const [userAsked, setUserAsked] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const wrapRef = useRef(null);
  const messagesEndRef = useRef(null);
  const currentBotMessage = useRef('');
  const pendingUserScroll = useRef(false);
  const [isStreaming, setIsStreaming] = useState(false);

  // Scroll positioning refs
  const scrollAttemptsLeft = useRef(0);
  const bottomSpacerRef = useRef(null);
  const spacerActiveRef = useRef(false);
  const programmaticScrollRef = useRef(false);

  useEffect(() => {
    const styleId = 'bot-chat-predefined-jump-style';
    const css = `
        @keyframes jump {
          0%   { transform: translateY(0); }
          20%  { transform: translateY(-10px); }
          40%  { transform: translateY(4px); }
          60%  { transform: translateY(-6px); }
          80%  { transform: translateY(2px); }
          100% { transform: translateY(0); }
        }
        .predefined-question {
          border: 1px solid #e8e8e8;
          transition: border-color .4s ease, background-color .25s, transform .2s;
        }
        .predefined-question.jump {
          animation: jump .9s ease;
          border-color: #555 !important;
        }
      `;
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = css;
  }, [accent]);

  useEffect(() => {
    if (!bot) return;

    const sid = `portal-${Date.now()}-${Math.floor(Math.random() * 10000) + 1}`;
    setSessionId(sid);

    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${encodeURIComponent(bot)}`
        );
        if (!res.ok) throw new Error();
        const data = await res.json();

        const welcomeMessages = Array.isArray(data.welcome_message)
          ? data.welcome_message
          : data.welcome_message
          ? [data.welcome_message]
          : [t('chat_welcome_default')];
        setMessages(welcomeMessages.map((t) => ({ from: 'bot', text: t })));

        let pq = data.predefined_questions ?? [];
        try {
          if (typeof pq === 'string') pq = JSON.parse(pq);
        } catch {}
        const hasReal =
          Array.isArray(pq) && (pq.length > 1 || (pq.length === 1 && pq[0].trim() !== ''));
        setPredefinedQuestions(hasReal ? pq : []);
      } catch {
        setMessages([{ from: 'bot', text: t('chat_welcome_load_failed') }]);
      } finally {
        setLoading(false);
      }
    })();
  }, [bot]);

  useEffect(() => {
    if (!userAsked && predefinedQuestions.length) {
      const id = setInterval(
        () => setAnimateIndex((i) => (i + 1) % predefinedQuestions.length),
        2000
      );
      return () => clearInterval(id);
    }
  }, [userAsked, predefinedQuestions]);

  // Helper: recalculate spacer so user message stays at top.
  const recalcSpacer = useCallback(() => {
    if (!bottomSpacerRef.current || !wrapRef.current) return 0;
    const container = wrapRef.current;
    const allUserMsgs = container.querySelectorAll('.user-message');
    const lastUserMsg = allUserMsgs[allUserMsgs.length - 1];
    if (!lastUserMsg) { bottomSpacerRef.current.style.height = '0px'; spacerActiveRef.current = false; return 0; }

    const spacer = bottomSpacerRef.current;
    const containerH = container.clientHeight;
    const msgOffsetTop = lastUserMsg.offsetTop;
    const naturalContentH = spacer.offsetTop;
    const contentBelowUserMsg = naturalContentH - msgOffsetTop;

    const neededSpacer = Math.max(0, containerH - contentBelowUserMsg);
    spacer.style.height = neededSpacer + 'px';
    spacerActiveRef.current = neededSpacer > 0;
    return neededSpacer;
  }, []);

  // Initial scroll positioning — ONLY runs right after user hits send.
  useLayoutEffect(() => {
    let rafId;
    if (pendingUserScroll.current) {
      pendingUserScroll.current = false;
      scrollAttemptsLeft.current = 6;
    }

    if (scrollAttemptsLeft.current > 0 && wrapRef.current) {
      scrollAttemptsLeft.current -= 1;

      const container = wrapRef.current;
      const allUserMsgs = container.querySelectorAll('.user-message');
      const target = allUserMsgs[allUserMsgs.length - 1];
      if (target) {
        const measureAndSizeSpacer = () => {
          if (!bottomSpacerRef.current || !wrapRef.current) return;
          const c = wrapRef.current;
          const containerH = c.clientHeight;

          bottomSpacerRef.current.style.height = '0px';
          const naturalH = bottomSpacerRef.current.offsetTop;
          const msgOffsetTop = target.offsetTop;
          const contentBelowMsg = naturalH - msgOffsetTop;

          const spacerH = Math.max(0, containerH - contentBelowMsg);
          bottomSpacerRef.current.style.height = spacerH + 'px';
          spacerActiveRef.current = spacerH > 0;
        };

        measureAndSizeSpacer();

        rafId = requestAnimationFrame(() => {
          measureAndSizeSpacer();
          if (!wrapRef.current) return;
          const c = wrapRef.current;
          programmaticScrollRef.current = true;
          c.scrollTo({ top: c.scrollHeight - c.clientHeight, behavior: 'smooth' });
        });
      }
    }
    return () => { if (rafId) cancelAnimationFrame(rafId); };
  }, [messages, loading, userAsked]);

  // Silently shrink spacer as bot response grows.
  useLayoutEffect(() => {
    if (scrollAttemptsLeft.current > 0) return;
    if (!spacerActiveRef.current) return;
    recalcSpacer();
  }, [messages, isStreaming, loading, recalcSpacer]);

  // Scroll-down button visibility
  const updateScrollDownVisibility = useCallback(() => {
    const container = wrapRef.current;
    if (!container) return;
    if (scrollAttemptsLeft.current > 0 || programmaticScrollRef.current) {
      setShowScrollDown(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 40;
    setShowScrollDown(!isAtBottom && scrollHeight > clientHeight);
  }, []);

  useEffect(() => {
    updateScrollDownVisibility();
  }, [messages, loading, updateScrollDownVisibility]);

  const handleContainerScroll = useCallback(() => {
    if (programmaticScrollRef.current) {
      const c = wrapRef.current;
      if (c && c.scrollHeight - c.scrollTop - c.clientHeight < 2) {
        programmaticScrollRef.current = false;
      }
      return;
    }
    if (scrollAttemptsLeft.current > 0) {
      scrollAttemptsLeft.current = 0;
    }
    updateScrollDownVisibility();
  }, [updateScrollDownVisibility]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const sendMessage = async (override) => {
    const trimmed =
      typeof override === 'string' ? override : userInput.trim();
    if (!trimmed || loading) return;

    // ── add message from user ─────────────────────────────
    if (!userAsked) setUserAsked(true);
    setMessages((p) => [...p, { from: 'user', text: trimmed }]);
    pendingUserScroll.current = true;
    if (typeof override !== 'string') setUserInput('');

    setLoading(true);
    currentBotMessage.current = '';
    setIsStreaming(true);

    const url = `${process.env.REACT_APP_API_BASE_URL}chatbot_response?user_input=${encodeURIComponent(
      trimmed
    )}&session_id=${sessionId}&bot_id=${encodeURIComponent(
      bot
    )}&language=english`;

    // helper to insert or update the bot reply
    const upsertBotMessage = () =>
      setMessages((msgs) => {
        const last = msgs[msgs.length - 1];
        if (last?.from === 'bot') {
          return [
            ...msgs.slice(0, -1),
            { ...last, text: currentBotMessage.current },
          ];
        }
        return [...msgs, { from: 'bot', text: currentBotMessage.current }];
      });

    return new Promise((resolve) => {
      const es = new EventSource(url);
      let firstChunk = true;
      let hasError = false;

      // streamed content
      es.onmessage = ({ data }) => {
        if (data === 'end of response') return; // final sentinel
        if (firstChunk) {
          setLoading(false);
          firstChunk = false;
        }
        currentBotMessage.current += data.replace(/<newline>/g, '\n');
        upsertBotMessage();
      };

      // server tells us it's done
      es.addEventListener('end', () => {
        if (!hasError) {
          upsertBotMessage();
        }
        es.close();
        setLoading(false);
        setIsStreaming(false);
        resolve();
      });

      // server‑side error event (rate limit, timeout, etc.)
      es.addEventListener('error', (e) => {
        hasError = true;
        es.close();
        setLoading(false);
        setIsStreaming(false);

        const errorMessages = buildErrorMessages(t);
        let errorMessage = errorMessages.default;
        try {
          if (e?.data) {
            // Handle legacy format (plain string)
            if (e.data === 'Timeout while generating response') {
              errorMessage = errorMessages.timeout;
            } else if (e.data === 'Internal server error') {
              errorMessage = errorMessages.internal_error;
            } else {
              // Try to parse as JSON (new format)
              const errorData = JSON.parse(e.data);
              errorMessage = errorMessages[errorData.type] || errorData.message || errorMessages.default;
            }
          }
        } catch {
          // If parsing fails, use default message
        }
        
        setMessages((p) => [
          ...p,
          { from: 'bot', text: errorMessage },
        ]);
        resolve();
      });

      // Handle EventSource connection errors (network issues, etc.)
      es.onerror = () => {
        // Only handle if not already handled by 'error' event listener
        if (hasError) return;

        // EventSource will auto-reconnect by default - we don't want that for errors
        if (es.readyState === EventSource.CLOSED) {
          hasError = true;
          setLoading(false);
          setIsStreaming(false);
          setMessages((p) => [
            ...p,
            { from: 'bot', text: t('chat_err_default') },
          ]);
          resolve();
        } else if (es.readyState === EventSource.CONNECTING) {
          // Connection lost, close and show error
          hasError = true;
          es.close();
          setLoading(false);
          setIsStreaming(false);
          setMessages((p) => [
            ...p,
            { from: 'bot', text: t('chat_connection_lost') },
          ]);
          resolve();
        }
      };
    });
  };

  const Link = (props) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  );

  return (
    <div
      className="chat-parent"
      style={{
        maxHeight: height,
        height: height,
        margin: '15px',
        marginTop: '20px',
        borderRadius: '10px',
        boxShadow: '0 0 16px rgba(0,0,0,.3)'
      }}
    >
      <div className="logo-messages-container">
        <div className="message-container" ref={wrapRef} onScroll={handleContainerScroll}>
          {messages.map((m, i) => {
            const showBotIcon = m.from === 'bot' && (i === 0 || messages[i - 1].from !== 'bot');
            return (
              <div
                key={i}
                className={`message ${m.from === 'user' ? 'user-message' : 'bot-message'}`}
              >
                {showBotIcon && (
                  <div className="scf-icon-container">
                    <div className="bot-left-icon" style={{ backgroundColor: accent }}>
                      <div className="bot-left-icon-text">
                        <p style={{ fontSize: 12 }}>AI</p>
                        <p style={{ fontSize: 8, fontWeight: 600 }}>{t('chat_bot_avatar_label')}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className={m.from === 'user' ? 'user-chat-text' : 'bot-chat-text'}>
                  <ReactMarkdown
                    remarkPlugins={[[remarkGfm], [remarkBreaks]]}
                    rehypePlugins={[rehypeRaw]}
                    components={{ a: Link }}
                  >
                    {m.text}
                  </ReactMarkdown>
                </div>
                {m.from === 'user' && (
                  <div className="user-icon-container">
                    {/* Absolute path — see the note in chatModal.js: '../' resolves
                        against the current URL and breaks on deeper routes. */}
                    <img src="/user-icon.png" alt={t('chat_user_avatar_alt')} className="user-icon" />
                  </div>
                )}
              </div>
            );
          })}
          {loading && (
            <div className="loading-dots">
              <div></div>
              <div></div>
              <div></div>
            </div>
          )}
          <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
          <div ref={messagesEndRef} />
          <style>
            {`
              @media (max-width: 480px) {
                .message-container {
                    margin-right: -40px !important;
                }
              }
            `}
          </style>
        </div>
        {showScrollDown && (
          <button
            className="scroll-to-bottom-btn"
            onClick={scrollToBottom}
            aria-label={t('chat_scroll_to_latest')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="14" height="14">
              <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" fill="currentColor"/>
            </svg>
          </button>
        )}
      </div>

      {predefinedQuestions.length > 0 && (
        <div
          className="predefined-container"
          style={{
            display: 'flex',
            flexWrap: 'wrap-reverse',
            justifyContent: 'flex-end',
            gap: '8px',
            padding: '6px 10px',
            paddingBottom: '0',
            marginBottom: '10px'
          }}
        >
          {predefinedQuestions.map((q, i) => (
            <div
              key={i}
              className={`predefined-question${
                !userAsked && i === animateIndex ? ' jump' : ''
              }`}
              onClick={() => sendMessage(q)}
              style={{
                flex: '0 0 auto',
                background: 'transparent',
                color: '#333',
                borderRadius: '20px',
                padding: '6px 12px',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'border-color .4s ease, background-color .25s, transform .2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f1f1';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {q}
            </div>
          ))}
        </div>
      )}

      <div className="chat-input">
        <textarea
          ref={(el) => {
            if (el) {
              el.style.height = 'auto';
              const lineHeight = 22; // ~16px * 1.4 line-height
              const maxLines = 5;
              const padding = 12; // 6px top + 6px bottom
              const maxHeight = (lineHeight * maxLines) + padding;
              const minHeight = lineHeight + padding; // single line + padding
              const newHeight = el.value ? Math.min(el.scrollHeight, maxHeight) : minHeight;
              el.style.height = newHeight + 'px';
              // Only show scrollbar when content exceeds max height
              if (el.scrollHeight > maxHeight) {
                el.classList.add('has-overflow');
              } else {
                el.classList.remove('has-overflow');
              }
            }
          }}
          rows={1}
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !loading) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder={t('chat_input_placeholder')}
          disabled={loading}
        />
        <button
          onClick={() => sendMessage()}
          disabled={!userInput.trim() || loading}
          className="input-button"
          style={{ backgroundColor: accent }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="send-icon">
            <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z"/>
          </svg>
        </button>
        <style>
          {`
            @media (max-width: 480px) {
              .chat-input {
                  width: 100% !important;
              }
            }
          `}
        </style>
      </div>
      <style>
        {`
          @media (max-width: 480px) {
            .chat-parent {
                max-height: none !important;
                margin: 6px !important;
            }
          }
        `}
      </style>
    </div>
  );
}
